import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import {
  Flame,
  Wind,
  Plug,
  Factory,
  Truck,
  Snowflake,
  Leaf,
  Trash2,
} from "lucide-react";
import { useDelayedFlag, useTran } from "@/hooks";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { useKpiTargetsStore, useCarbonTargetsStore } from "../hooks";
import TargetYearCard from "./TargetYearCard";
import CarbonInventorySkeleton from "./skeleton/CarbonInventorySkeleton";

const scopeConfigs = [
  {
    id: "scope1",
    title: "Scope 1 - Direct Emissions",
    description: "If unknown, enter activity data and attach bills or logs.",
    icon: <Flame className="h-4 w-4" />,
    accent: "emerald",
    fields: [
      {
        key: "stationaryCombustion",
        label: "Stationary combustion (diesel, gas, LPG)",
        placeholder: "e.g., 1,000",
        icon: <Factory className="h-4 w-4" />,
      },
      {
        key: "mobileCombustion",
        label: "Mobile combustion (company vehicles, forklifts)",
        placeholder: "e.g., 750",
        icon: <Truck className="h-4 w-4" />,
      },
      {
        key: "fugitiveEmissions",
        label: "Fugitive emissions (refrigerants/AC leaks)",
        placeholder: "e.g., 25",
        icon: <Snowflake className="h-4 w-4" />,
      },
      {
        key: "processEmissions",
        label: "Process emissions (industrial gases)",
        placeholder: "e.g., 150",
        icon: <Wind className="h-4 w-4" />,
      },
    ],
  },
  {
    id: "scope2",
    title: "Scope 2 - Purchased Energy",
    description: "Enter electricity data purchased. Attach monthly bills.",
    icon: <Plug className="h-4 w-4" />,
    accent: "blue",
    fields: [
      {
        key: "purchasedElectricity",
        label: "Purchased electricity (grid or renewable)",
        placeholder: "e.g., 145,000",
        icon: <Plug className="h-4 w-4" />,
      },
      {
        key: "purchasedSteam",
        label: "Purchased steam/heat",
        placeholder: "e.g., 38,000",
        icon: <Leaf className="h-4 w-4" />,
      },
      {
        key: "purchasedCooling",
        label: "Purchased cooling or chilled water",
        placeholder: "e.g., 26,000",
        icon: <Snowflake className="h-4 w-4" />,
      },
    ],
  },
];

const CarbonInventoryTarget = observer(() => {
  const { t } = useTran();
  const { selectedYear } = useKpiTargetsStore();
  const {
    getCarbonTargets,
    loadCarbonTargets,
    saveCarbonTargets,
    setCarbonValue,
    loading,
  } = useCarbonTargetsStore();
  const currentYear = selectedYear;
  const valuesByScope = getCarbonTargets(currentYear);

  useEffect(() => {
    loadCarbonTargets(currentYear);
  }, [loadCarbonTargets, currentYear]);

  const showSkeleton = useDelayedFlag(loading);

  if (showSkeleton) {
    return <CarbonInventorySkeleton />;
  }

  return (
    <div className="space-y-6">
      {loading ? (
        <p className="text-xs text-muted-foreground">
          {t("Loading carbon inventory targets...")}
        </p>
      ) : null}
      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-radius border border-border bg-card p-5 shadow-sm">
          <p className="text-lg font-semibold text-foreground">
            {t("Carbon Inventory")}
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            {t(
              "Capture Scope 1 and Scope 2 targets by entering the activity data you have on hand.",
            )}
          </p>
        </div>
        <TargetYearCard />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        {scopeConfigs.map((scope) => {
          const defaultUnit = scope.id === "scope1" ? "tCO2e" : "kWh";
          return (
            <div
              key={scope.id}
              className="space-y-4 rounded-radius border border-border bg-card p-6 shadow-sm"
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex items-start gap-3">
                  <div
                    className={cn(
                      "flex h-10 w-10 items-center justify-center rounded-full text-white",
                      scope.accent === "emerald"
                        ? "bg-emerald-500/90"
                        : "bg-blue-500/90",
                    )}
                  >
                    {scope.icon}
                  </div>
                  <div>
                    <p className="text-base font-semibold text-foreground">
                      {t(scope.title)}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {t(scope.description)}
                    </p>
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {scope.fields.map((field) => (
                  <div key={field.key} className="space-y-1">
                    <div className="flex items-center gap-2 text-foreground">
                      <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-muted-foreground">
                        {field.icon}
                      </span>
                      <p className="text-sm font-semibold">{t(field.label)}</p>
                    </div>
                    <label className="text-xs font-medium text-muted-foreground">
                      {t("Annual Target")} ({currentYear})
                    </label>
                    <div className="flex items-center gap-3">
                      <Input
                        value={valuesByScope[scope.id][field.key] || ""}
                        placeholder={t(field.placeholder)}
                        className="h-11 flex-1 text-sm"
                        onChange={(event) =>
                          setCarbonValue(
                            scope.id,
                            field.key,
                            event.target.value,
                            currentYear,
                          )
                        }
                      />
                      <span className="min-w-[72px] text-sm font-semibold text-muted-foreground">
                        {defaultUnit}
                      </span>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="text-destructive hover:text-destructive"
                        onClick={async () => {
                          setCarbonValue(scope.id, field.key, "", currentYear);
                          await saveCarbonTargets(currentYear);
                        }}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
});

export default CarbonInventoryTarget;
